import { Skeleton } from "antd";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { BTDropdown, BTDropdownButton } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import {
    BdsContentHeader,
    BdsIconDotsThree,
    BdsIconShare,
    BdsIconX,
    BdsLayout,
} from "@buildertrend/components";

import { usePermission } from "helpers/globalContext/usePermission";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";
import { queryClient } from "helpers/queryClient";

import { BTLoginTypes } from "types/enum";

import { showApixErrorMessage } from "utilities/api/apix-handler";

import { AuditInfoButton } from "commonComponents/AuditInfoButton/AuditInfoButton";
import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { btDeleteConfirm } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { EditableTitle } from "commonComponents/utilities/EditableTitle/EditableTitle";
import { ShowOnPortal } from "commonComponents/utilities/ShowOnPortal/ShowOnPortal";
import TextTruncate from "commonComponents/utilities/TextTruncate/TextTruncate";

import { getPlansGetV2QueryKey, usePlansRenameSetPutV2 } from "handlers";

import { type PlanPage } from "handlers/models/planPage";
import { type PlanSet } from "handlers/models/planSet";

import { DownloadPlanSetIconDropdown } from "entity/Plans/DownloadPlanSetButton/DownloadPlanSetIconDropdown";
import { useDownloadPlanSet } from "entity/Plans/DownloadPlanSetButton/useDownloadPlanSet";
import {
    PENDING_REVISION_TOOLTIP,
    usePendingRevisionSetIds,
} from "entity/Plans/hooks/usePendingRevisionSetIds";
import { PlanRevisionsBanner } from "entity/Plans/PlanRevisionsReview/PlanRevisionsBanner";
import { PlansCard } from "entity/Plans/PlansCard/PlansCard";
import { PlansCardSkeleton } from "entity/Plans/PlansCard/PlansCardSkeleton";
import { SharePlanType } from "entity/Plans/PlansList/PlansList";

import "./PlanPagesList.less";

interface IPlanPagesListProps {
    planPages: PlanPage[];
    isLoading: boolean;
    planSet: PlanSet;
    onBack?: () => void;
    useMobileLayout?: boolean;
    displayJobName?: string;
    onHandlePlanClick?: (plan: PlanPage) => void;
    shareToken?: string;
    scrollablePanelRef?: React.RefObject<HTMLDivElement>;
    onDeleteSet?: (setId: string) => Promise<void>;
    onDeletePage?: (pageId: string) => Promise<void>;
    jobId: number;
    refetch: () => void;
    onViewVersionHistory?: (plan: PlanPage) => void;
    onUploadNewVersion?: (plan: PlanPage) => void;
    onOrganize?: () => void;
    onUploadRevision?: (planSet: PlanSet) => void;
    onCopyShareLink: (id?: string, shareType?: SharePlanType) => Promise<void>;
    onGenerateQrCodeLink: (
        shareType: SharePlanType,
        id?: string,
        qrCodeTitle?: string,
    ) => Promise<void>;
}

export const PlanPagesList = ({
    planPages,
    planSet,
    isLoading,
    onBack,
    useMobileLayout,
    displayJobName,
    onHandlePlanClick,
    shareToken,
    scrollablePanelRef,
    onDeleteSet,
    onDeletePage,
    jobId,
    refetch,
    onViewVersionHistory,
    onUploadNewVersion,
    onOrganize,
    onUploadRevision,
    onCopyShareLink,
    onGenerateQrCodeLink,
}: IPlanPagesListProps) => {
    const {
        setId: planSetId,
        name: planSetName,
        createdByUserName,
        createdDate,
        updatedByUserName,
        updatedDate,
    } = planSet;
    const [isEditing, setIsEditing] = useState(false);
    const [localSetName, setLocalSetName] = useState(planSetName);
    const canUpdate = usePermission("specifications", "canUpdate");
    const canDelete = usePermission("specifications", "canDelete");
    const userContext = useContext(UserInfoContext);
    const setHasAnnotations = planPages.some((p) => p.hasAnnotations);

    useEffect(() => {
        setLocalSetName(planSetName);
    }, [planSetName]);

    const { mutateAsync: renamePlanSet } = usePlansRenameSetPutV2();

    const maxNameLength = 100;
    const handleRename = useCallback(
        async (newName: string) => {
            const finalValue = (newName ?? "").slice(0, maxNameLength).trim();
            if (finalValue === planSetName || !finalValue) {
                if (!newName) {
                    setLocalSetName(planSetName);
                }
                return;
            }

            try {
                await renamePlanSet({
                    data: { newName: finalValue, setId: planSetId },
                    params: { jobId: jobId },
                });
                await queryClient.invalidateQueries(getPlansGetV2QueryKey({ jobId: jobId }));
                refetch();
                setLocalSetName(finalValue);
            } catch (error) {
                showApixErrorMessage(error as Error, "Failed to rename plan");
            }
        },
        [planSetName, planSetId, renamePlanSet, jobId, refetch],
    );

    const handleDeleteSet = useCallback(() => {
        btDeleteConfirm({
            title: "Delete plan",
            entityName: "plan",
            onOk: async () => {
                if (onDeleteSet) {
                    try {
                        await onDeleteSet(planSetId);
                    } catch (e) {
                        showApixErrorMessage(e as Error);
                    }
                }
            },
        });
    }, [onDeleteSet, planSetId]);

    const handleOnTitleChange = useCallback((value: string) => {
        // onChange is called during editing
        setLocalSetName(value);
    }, []);

    const handleTitleBlur = useCallback(
        async (value: string) => {
            await handleRename(value);
            setIsEditing(false);
        },
        [handleRename],
    );

    const handleTitleClick = useCallback(() => {
        if (canUpdate) {
            setIsEditing(true);
        }
    }, [canUpdate]);

    const plansSetRenameListeners = useRef<Record<string, () => void>>({});
    const setEditPlanSetNameListener = useCallback((id: string, callback: () => void) => {
        plansSetRenameListeners.current[id] = callback;
    }, []);

    const handleRenameSetClick = useCallback(() => {
        plansSetRenameListeners.current[planSetId]?.();
        setIsEditing(true);
    }, [planSetId]);

    const planPageRenamelisteners = useRef<Record<string, () => void>>({});
    const setEditPlanPageNameListener = useCallback((id: string, callback: () => void) => {
        planPageRenamelisteners.current[id] = callback;
    }, []);

    const handleOnCancelNameChange = useCallback(() => {
        setLocalSetName(planSetName);
        setIsEditing(false);
    }, [planSetName]);

    const handleOrganizeClick = useCallback(() => {
        onOrganize?.();
    }, [onOrganize]);

    const handleUploadRevisionClick = useCallback(() => {
        onUploadRevision?.(planSet);
    }, [onUploadRevision, planSet]);

    // Shares its query key with PlanRevisionsBanner (rendered below for this same set/job) via
    // usePendingRevisionSetIds, so this reuses the cached result instead of firing a second request.
    const pendingRevisionSetIds = usePendingRevisionSetIds(
        jobId,
        [planSetId],
        !!onUploadRevision && !!planSetId,
    );
    const hasPendingRevision = pendingRevisionSetIds.has(planSetId);

    const handleShareSet = useCallback(async () => {
        await onCopyShareLink?.(planSet.setId, SharePlanType.SET);
    }, [onCopyShareLink, planSet.setId]);

    const handleGenerateQrCodeSet = useCallback(async () => {
        await onGenerateQrCodeLink?.(SharePlanType.SET, planSet.setId, planSet.name);
    }, [onGenerateQrCodeLink, planSet.setId, planSet.name]);

    const showRename = canUpdate;
    const showOrganize = canUpdate && planPages.length >= 2;
    const showUploadRevision = canUpdate && !!onUploadRevision;
    const canDownload = !shareToken && planPages.length > 0 && !!planSetId;

    const { download: downloadPlanSet, isWorking: isDownloading } = useDownloadPlanSet({
        setId: planSetId,
        jobId,
        shareToken,
    });
    const handleDownloadBase = useCallback(() => {
        void downloadPlanSet(false);
    }, [downloadPlanSet]);
    const handleDownloadMarkups = useCallback(() => {
        void downloadPlanSet(true);
    }, [downloadPlanSet]);

    const planSetTitle = canUpdate ? (
        <EditableTitle
            id={planSetId}
            value={localSetName}
            onChange={handleOnTitleChange}
            onCancel={handleOnCancelNameChange}
            onBlur={handleTitleBlur}
            data-testid={`plansSetItemTitle-${planSetId}`}
            className={cn("plansSetEditableTitle", { editing: isEditing })}
            setEditListener={setEditPlanSetNameListener}
            useTextTruncate
            maxRows={1}
        />
    ) : (
        <TextTruncate className="plan-set-name-readonly" data-testid={`planPageTitle-${planSetId}`}>
            {planSetName}
        </TextTruncate>
    );

    return (
        <div
            className={cn("plans-viewer", { "mobile-layout": useMobileLayout })}
            data-testid="plans-viewer"
        >
            <BdsLayout displayType="HCF" useMaxWidth className="viewer-layout">
                <BdsContentHeader
                    id="plans-viewer-header"
                    testid="plans-viewer-header"
                    title={
                        isLoading ? (
                            <Skeleton
                                active
                                paragraph={false}
                                title={{ style: { margin: 0, height: 36 } }}
                                className="header-skeleton"
                            />
                        ) : (
                            <div className="editableTitleContainer" onClick={handleTitleClick}>
                                {planSetTitle}
                            </div>
                        )
                    }
                    jobName={displayJobName}
                    slotTop={
                        <>
                            <ShowOnPortal
                                builder
                                btadmin
                                render={() =>
                                    // Mirrors the menu items' own conditions below (canDownload,
                                    // showUploadRevision, showOrganize, showRename, canDelete) so the
                                    // "More" button itself hides once every item it would open is hidden.
                                    (showRename ||
                                        showOrganize ||
                                        showUploadRevision ||
                                        canDelete ||
                                        canDownload) && (
                                        <BTDropdown
                                            testid={`plan-set-header-context-menu-${planSetId}`}
                                            placement="bottomRight"
                                            overlay={
                                                <BTMenu
                                                    testid={`plan-set-header-menu-${planSetId}`}
                                                >
                                                    {canDownload &&
                                                        (setHasAnnotations ? (
                                                            <BTMenu.SubMenu
                                                                testid={`download-set-submenu-${planSetId}`}
                                                                key={`downloadSet-${planSetId}`}
                                                                title="Download"
                                                                disabled={isDownloading}
                                                            >
                                                                <BTMenu.Item
                                                                    testid={`download-set-base-${planSetId}`}
                                                                    key={`downloadSetBase-${planSetId}`}
                                                                    onClick={handleDownloadBase}
                                                                    aria-label="Download without annotations"
                                                                >
                                                                    Base
                                                                </BTMenu.Item>
                                                                <BTMenu.Item
                                                                    testid={`download-set-markups-${planSetId}`}
                                                                    key={`downloadSetMarkups-${planSetId}`}
                                                                    onClick={handleDownloadMarkups}
                                                                    aria-label="Download with annotations"
                                                                >
                                                                    Markups
                                                                </BTMenu.Item>
                                                            </BTMenu.SubMenu>
                                                        ) : (
                                                            <BTMenu.Item
                                                                testid={`download-set-${planSetId}`}
                                                                key={`downloadSet-${planSetId}`}
                                                                onClick={handleDownloadBase}
                                                                disabled={isDownloading}
                                                                aria-label="Download plan set"
                                                            >
                                                                Download
                                                            </BTMenu.Item>
                                                        ))}
                                                    {showUploadRevision && (
                                                        <BTMenu.Item
                                                            testid={`upload-revision-${planSetId}`}
                                                            key={`uploadRevision-${planSetId}`}
                                                            onClick={
                                                                hasPendingRevision
                                                                    ? undefined
                                                                    : handleUploadRevisionClick
                                                            }
                                                            disabled={hasPendingRevision}
                                                            aria-label="Upload revision"
                                                        >
                                                            <BTPopover
                                                                content={PENDING_REVISION_TOOLTIP}
                                                                placement="left"
                                                                disabled={!hasPendingRevision}
                                                            >
                                                                <span>Upload revision</span>
                                                            </BTPopover>
                                                        </BTMenu.Item>
                                                    )}
                                                    {showOrganize && (
                                                        <BTMenu.Item
                                                            testid={`organize-pages-${planSetId}`}
                                                            key={`organizePages-${planSetId}`}
                                                            onClick={handleOrganizeClick}
                                                            aria-label="Organize pages"
                                                        >
                                                            Organize pages
                                                        </BTMenu.Item>
                                                    )}
                                                    {showRename && (
                                                        <BTMenu.Item
                                                            testid={`rename-set-${planSetId}`}
                                                            key={`renamePage-${planSetId}`}
                                                            onClick={handleRenameSetClick}
                                                            aria-label="Rename plan set"
                                                        >
                                                            Rename
                                                        </BTMenu.Item>
                                                    )}
                                                    {canDelete && (
                                                        <BTMenu.Item
                                                            testid={`deletePlanSet-${planSetId}`}
                                                            key={`deletePlanSet-${planSetId}`}
                                                            onClick={handleDeleteSet}
                                                            className={cn({
                                                                DeletePlanDivider: showRename,
                                                            })}
                                                            aria-label="Delete plan"
                                                        >
                                                            <div className="DeletePlan">
                                                                Delete plan
                                                            </div>
                                                        </BTMenu.Item>
                                                    )}
                                                </BTMenu>
                                            }
                                        >
                                            <BTPopover content="More">
                                                <BTButton
                                                    icon={<BdsIconDotsThree />}
                                                    aria-label="More"
                                                    data-testid={`plan-set-header-context-menu-btn-${planSetId}`}
                                                    type="tertiary"
                                                />
                                            </BTPopover>
                                        </BTDropdown>
                                    )
                                }
                            />
                            <ShowOnPortal
                                subs
                                render={() =>
                                    canDownload && (
                                        <DownloadPlanSetIconDropdown
                                            setId={planSet.setId}
                                            jobId={jobId}
                                            setHasAnnotations={setHasAnnotations}
                                        />
                                    )
                                }
                            />
                            <AuditInfoButton
                                id={`plan-set-info-button-${planSetId}`}
                                displayType="tertiary"
                                userDetails={{
                                    addedByUserName: createdByUserName,
                                    addedByUserDate: createdDate,
                                    updatedByUserName: updatedByUserName,
                                    updatedByUserDate: updatedDate,
                                }}
                            />
                            {!shareToken &&
                                (userContext?.loginType === BTLoginTypes.BUILDER ? (
                                    <BTDropdownButton
                                        data-testid={`set-share-btn-${planSetId}`}
                                        type="tertiary"
                                        showCaret={false}
                                        buttonProps={{
                                            icon: <BdsIconShare />,
                                            "aria-label": "Share plan set dropdown",
                                            className: "CompactShare ShareSection",
                                        }}
                                        menuItems={[
                                            {
                                                "data-testid": `set-share-link-${planSetId}`,
                                                content: "Copy share link",
                                                onClick: handleShareSet,
                                                "aria-label": "Copy plan set link",
                                            },
                                            {
                                                "data-testid": `set-qr-code-${planSetId}`,
                                                content: "Create QR code",
                                                onClick: handleGenerateQrCodeSet,
                                                "aria-label": "Create plan set QR code",
                                            },
                                        ]}
                                    />
                                ) : (
                                    <BTButton
                                        data-testid={`plan-share-link-${planSetId}`}
                                        icon={<BdsIconShare />}
                                        aria-label="Copy plan set link"
                                        className="CompactShare ShareSection"
                                        onClick={handleShareSet}
                                        type="tertiary"
                                    />
                                ))}
                            {onBack && (
                                <BTButton
                                    onClick={onBack}
                                    data-testid="plans-viewer-close-button"
                                    icon={<BdsIconX />}
                                    type="tertiary"
                                    aria-label="Close"
                                />
                            )}
                        </>
                    }
                />
                <div className="viewer-content" ref={scrollablePanelRef}>
                    {canUpdate &&
                        !shareToken &&
                        planSet?.setId &&
                        userContext?.loginType === BTLoginTypes.BUILDER && (
                            <PlanRevisionsBanner setId={planSet.setId} jobId={jobId} />
                        )}
                    {isLoading ? (
                        <div className="plans-grid">
                            {Array.from({ length: 4 }).map((_, index) => (
                                <PlansCardSkeleton
                                    key={`skeleton-${index}`}
                                    data-testid={`plansCardSkeleton-${index}`}
                                />
                            ))}
                        </div>
                    ) : (
                        planPages && (
                            <div className="plans-grid">
                                {planPages.map((plan: PlanPage) => (
                                    <PlansCard
                                        key={plan.pageId}
                                        plan={plan}
                                        onClick={onHandlePlanClick}
                                        onDeletePage={onDeletePage}
                                        shareToken={shareToken}
                                        jobId={jobId}
                                        refetch={refetch}
                                        setEditPlanPageNameListener={setEditPlanPageNameListener}
                                        planPageRenamelisteners={planPageRenamelisteners}
                                        onViewVersionHistory={onViewVersionHistory}
                                        onUploadNewVersion={onUploadNewVersion}
                                        onCopySharePageLink={onCopyShareLink}
                                        onGenerateQrCodePage={onGenerateQrCodeLink}
                                        hasAnnotations={Boolean(plan.hasAnnotations)}
                                    />
                                ))}
                            </div>
                        )
                    )}
                </div>
            </BdsLayout>
        </div>
    );
};
